import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../../../lib/sheets";
import RaceSessionSelect from "../../../../components/RaceSessionSelect";

const ARCHIVE_SHEET_ID = "1YX4CzZlUxNIheMi_BKSoJivlo9M3g9cpcdPmqwtWroQ";
const ARCHIVE_SCHEDULE_RANGE = "archive_schedule!A1:D";

// ---------- Helpers ----------
function colIndexFromLetters(letters) {
    let n = 0;
    for (let i = 0; i < letters.length; i++) n = n * 26 + (letters.charCodeAt(i) - 64);
    return n;
}
function letterToIndex(letter) {
    return colIndexFromLetters(letter) - 1;
}
function colLettersFromIndex(n1) {
    let n = n1, out = "";
    while (n > 0) {
        const rem = (n - 1) % 26;
        out = String.fromCharCode(65 + rem) + out;
        n = Math.floor((n - 1) / 26);
    }
    return out;
}
function lettersBetween(start, end) {
    const s = colIndexFromLetters(start);
    const e = colIndexFromLetters(end);
    const list = [];
    for (let i = s; i <= e; i++) list.push(colLettersFromIndex(i));
    return list;
}
function safeVal(row, idx) {
    const v = row?.[idx];
    return v === null || v === undefined ? "" : String(v).trim();
}
function parseSignedIntLike(v) {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    const m = s.match(/[+-]?\d+/);
    if (!m) return null;
    const n = parseInt(m[0], 10);
    return Number.isNaN(n) ? null : n;
}
function parseLapTimeToSeconds(v) {
    const s = String(v ?? "").trim();
    if (!s) return null;

    const up = s.toUpperCase();
    if (up === "DNS" || up === "DNF" || up === "BAN") return null;

    const m = s.match(/^(\d+):(\d{2})(?:\.(\d{1,3}))?$/);
    if (m) {
        const min = Number(m[1]);
        const sec = Number(m[2]);
        const ms = m[3] ? Number(m[3].padEnd(3, "0")) : 0;
        return min * 60 + sec + ms / 1000;
    }

    const f = Number(s.replace(",", "."));
    return Number.isFinite(f) ? f : null;
}

const imgFlag = (src) => <img src={src} alt="Flag" className="flag" loading="lazy" />;
const imgTeam = (src) => <img src={src} alt="Team" className="teamlogo" loading="lazy" />;
const imgCar = (src) => <img src={src} alt="Car" className="carlogo" loading="lazy" />;

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function isSheetMissingResponse(res) {
    if (!res || typeof res !== "object") return true;
    const hasHeader = Array.isArray(res.header) && res.header.length > 0;
    const hasRows = Array.isArray(res.rows) && res.rows.length > 0;
    return !hasHeader && !hasRows;
}

// ---------- View-Builder ----------
function buildRaceView(header, rows) {
    const hIndex = (L) => letterToIndex(L);

    const visCols = lettersBetween("A", "N");
    const baseIdx = visCols.map((L) => hIndex(L));

    // Bilder im Archiv gleich wie im normalen Results-Race
    const idxFlagURL = hIndex("S");
    const idxCarURL = hIndex("T");
    const idxTeamURL = hIndex("U");

    const idxCarNo = hIndex("E");
    const idxDriver = hIndex("G");
    const idxTeam = hIndex("I");

    const posA = hIndex("A");
    const toD = hIndex("D") - posA;
    const toE = hIndex("E") - posA;
    const toF = hIndex("F") - posA;
    const toG = hIndex("G") - posA;
    const toH = hIndex("H") - posA;
    const toI = hIndex("I") - posA;

    const viewHeader = visCols.map((L) => header[hIndex(L)] ?? L);

    viewHeader[toE] = "#";
    if ((viewHeader[1] || "").toLowerCase() === "start pos") {
        viewHeader[1] = "Start";
    }

    const viewRows = rows.map((r) => {
        const row = baseIdx.map((ci) => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc) row[toD] = imgFlag(flagSrc);

        row[toE] = r[idxCarNo];

        const carSrc = safeVal(r, idxCarURL);
        if (carSrc) row[toF] = imgCar(carSrc);

        row[toG] = r[idxDriver];

        const teamSrc = safeVal(r, idxTeamURL);
        if (teamSrc) row[toH] = imgTeam(teamSrc);

        row[toI] = r[idxTeam];

        return row;
    });

    return { header: viewHeader, rows: viewRows };
}

function buildQualyPracticeView(header, rows) {
    const hIndex = (L) => letterToIndex(L);
    const visCols = lettersBetween("A", "G");
    const baseIdx = visCols.map((L) => hIndex(L));

    const idxFlagURL = hIndex("J");
    const idxAutoURL = hIndex("K");

    const targetB = hIndex("B");
    const targetD = hIndex("D");

    const viewHeader = visCols.map((L) => header[hIndex(L)] ?? L);

    const visibleCpos = 2;
    if ((viewHeader[visibleCpos] || "").toLowerCase().includes("fahrzeugnummer")) {
        viewHeader[visibleCpos] = "#";
    }

    const viewRows = rows.map((r) => {
        const row = baseIdx.map((ci) => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc) row[targetB - hIndex("A")] = imgFlag(flagSrc);

        const carSrc = safeVal(r, idxAutoURL);
        if (carSrc) row[targetD - hIndex("A")] = imgCar(carSrc);

        return row;
    });

    return { header: viewHeader, rows: viewRows };
}

// ✅ Archiv-Tabs
// Falls deine Namen minimal anders sind, nur diese Zeilen anpassen.
const archiveCfg = {
    RACE: {
        GESAMT: {
            fetchRange: (r) => `Copy of R${r}_RACE!A1:U100`,
            buildView: buildRaceView,
        },
        PRO: {
            // ALT: `R${r}_RACE!W1:AQ100`
            // NEU: eigener Tab, gleiche Struktur wie Gesamt
            fetchRange: (r) => `Copy of R${r}_RACEPRO!A1:U100`,
            buildView: buildRaceView,
        },
        AM: {
            // ALT: `R${r}_RACE!AS1:BM100`
            // NEU: eigener Tab, gleiche Struktur wie Gesamt
            fetchRange: (r) => `Copy of R${r}_RACEAM!A1:U100`,
            buildView: buildRaceView,
        },
    },

    QUALY: {
        GESAMT: {
            fetchRange: (r) => `Copy of R${r}_QUALY!A1:K100`,
            buildView: buildQualyPracticeView,
        },
        PRO: {
            fetchRange: (r) => `Copy of R${r}_QUALY!M1:W100`,
            buildView: buildQualyPracticeView,
        },
        AM: {
            fetchRange: (r) => `Copy of R${r}_QUALY!Y1:AI100`,
            buildView: buildQualyPracticeView,
        },
    },

    PRACTICE: {
        GESAMT: {
            fetchRange: (r) => `Copy of R${r}_PRACTICE!A1:K100`,
            buildView: buildQualyPracticeView,
        },
        PRO: {
            fetchRange: (r) => `Copy of R${r}_PRACTICE!M1:W100`,
            buildView: buildQualyPracticeView,
        },
        AM: {
            fetchRange: (r) => `Copy of R${r}_PRACTICE!Y1:AI100`,
            buildView: buildQualyPracticeView,
        },
    },
};

function pillForKlass(k) {
    const key = String(k || "GESAMT").toUpperCase();
    const cls =
        key === "PRO" ? "pill--pro" :
            key === "AM" ? "pill--am" :
                key === "INT" ? "pill--int" :
                    "pill--int";

    return <span className={`pill ${cls}`}>{key}</span>;
}

export default function ArchiveResults() {
    const [race, setRace] = useState(null);

    const [session, setSession] = useState("RACE");
    const [klass, setKlass] = useState("GESAMT");

    const [viewHeader, setViewHeader] = useState([]);
    const [viewRows, setViewRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        (async () => {
            if (!race) return;

            setLoading(true);
            setMsg("");
            setViewHeader([]);
            setViewRows([]);

            try {
                const sessionCfg = archiveCfg[session];
                if (!sessionCfg) {
                    setMsg("Ungültige Session-Auswahl.");
                    return;
                }

                const classCfg = sessionCfg[klass];
                if (!classCfg) {
                    setMsg("Ungültige Klassen-Auswahl.");
                    return;
                }

                const { fetchRange, buildView } = classCfg;
                const actualRange = fetchRange(race);

                console.log("ARCHIVE RESULTS sheetId:", ARCHIVE_SHEET_ID);
                console.log("ARCHIVE RESULTS range:", actualRange);

                const res = await fetchSheetViaProxy({
                    range: actualRange,
                    sheetId: ARCHIVE_SHEET_ID,
                });
                console.log("ARCHIVE RESULTS response:", res);
                if (isSheetMissingResponse(res)) {
                    setMsg("Für diese Archiv-Auswahl sind keine Ergebnisse vorhanden.");
                    return;
                }

                const header = Array.isArray(res?.header) ? res.header : [];
                const rows = Array.isArray(res?.rows)
                    ? res.rows.map((r) => header.map((h) => r[h]))
                    : [];

                const { header: vHeader, rows: vRows } = buildView(header, rows);
                setViewHeader(vHeader);
                setViewRows(vRows);
            } catch (e) {
                console.warn("Archive results load failed", e);
                setMsg("Für diese Archiv-Auswahl sind keine Ergebnisse vorhanden.");
            } finally {
                setLoading(false);
            }
        })();
    }, [race, session, klass]);

    const colIdxMap = useMemo(() => {
        const map = new Map();
        viewHeader.forEach((h, i) => map.set((h || "").toLowerCase(), i));
        return map;
    }, [viewHeader]);

    const deltaCol = useMemo(() => {
        if (session === "RACE") return 2;
        return colIdxMap.get("delta pos");
    }, [session, colIdxMap]);

    const numericCols = useMemo(() => {
        const set = new Set();
        viewRows.forEach((row) =>
            row.forEach((cell, i) => {
                if (isNumeric(cell)) set.add(i);
            })
        );
        return set;
    }, [viewRows]);

    const fastestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) =>
            String(h ?? "").toLowerCase().includes("fastest")
        );
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const fastestLapBest = useMemo(() => {
        if (fastestLapCol === null) return null;

        let best = null;
        for (const row of viewRows) {
            const t = parseLapTimeToSeconds(row?.[fastestLapCol]);
            if (t === null) continue;
            if (best === null || t < best) best = t;
        }
        return best;
    }, [viewRows, fastestLapCol]);

    const avgLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) =>
            String(h ?? "").toLowerCase().includes("avg")
        );
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const bestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) =>
            String(h ?? "").toLowerCase().includes("best")
        );
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    return (
        <>
            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                autoSelectFirst={true}
                valueSession={session}
                onSessionChange={(val) => {
                    setSession(val);
                    setKlass("GESAMT");
                }}
                showSession={true}
                valueClass={klass}
                onClassChange={setKlass}
                showClass={true}
                sheetId={ARCHIVE_SHEET_ID}
                scheduleRange={ARCHIVE_SCHEDULE_RANGE}
                raceLabelPrefix="R"
            />

            <div className="card overview-card-top">
                <h1>Archiv Season 1 – Ergebnisse</h1>
                <p className="muted results-subtitle">
                    {session} {pillForKlass(klass)}
                </p>
            </div>

            {msg && (
                <div className="card">
                    <p className="text-dim">{msg}</p>
                </div>
            )}

            {loading && (
                <div className="card">
                    <p>Lade…</p>
                </div>
            )}

            {!loading && !msg && (
                <div className="fancyTable">
                    <table className="tbl">
                        <thead>
                        <tr>
                            {viewHeader.map((h, i) => {
                                const isAvg = avgLapCol !== null && i === avgLapCol;
                                const isBest = bestLapCol !== null && i === bestLapCol;

                                return (
                                    <th key={i} className={(isAvg || isBest) ? "lap-center" : ""}>
                                        {h}
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>
                        <tbody>
                        {viewRows.map((r, ri) => (
                            <tr key={ri}>
                                {r.map((cell, ci) => {
                                    let cls = "";

                                    if (numericCols.has(ci)) cls = "num";

                                    if (typeof deltaCol === "number" && ci === deltaCol) {
                                        const n = parseSignedIntLike(cell);
                                        if (n !== null) {
                                            if (n > 0) cls = `${cls} delta-up`.trim();
                                            else if (n < 0) cls = `${cls} delta-down`.trim();
                                        }
                                    }

                                    const headerName = (viewHeader?.[ci] || "").toLowerCase();
                                    if (headerName.includes("pen") && String(cell).trim() !== "") {
                                        cls = `${cls} penalty-red`.trim();
                                    }

                                    if (fastestLapCol !== null && fastestLapBest !== null && ci === fastestLapCol) {
                                        const t = parseLapTimeToSeconds(cell);
                                        if (t !== null && Math.abs(t - fastestLapBest) < 0.0005) {
                                            cls = `${cls} fastest-best`.trim();
                                        }
                                    }

                                    if (avgLapCol !== null && ci === avgLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }
                                    if (bestLapCol !== null && ci === bestLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }

                                    return (
                                        <td key={ci} className={cls}>
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}